import { audio, buzz, CHAPTER_KEYS } from '../audio/audio';
import { MASS_KINDS, MASS_SPECS, WORLD_H, WORLD_W, colorName } from '../core/constants';
import { type LevelTrace, receiverNeeds } from '../core/level';
import { starCap, starsFor } from '../core/score';
import { encodeLevel } from '../core/sharecode';
import type { Level, Mass, MassKind } from '../core/types';
import { type LitChange, Session } from '../game/session';
import { store } from '../game/store';
import type { DragState } from '../render/board';
import { GOLD_BRIGHT } from '../render/palette';
import { trayIcon } from '../render/sprites';
import { LEVELS, levelLabel } from '../levels';
import { type Screen, app, markReady } from './app';
import { confirmDialog, openHelp, openSettings } from './dialogs';
import { ICONS, announce, h, icon } from './dom';
import { starIcons } from './levels';

export type GameMode = 'campaign' | 'endless' | 'custom' | 'test';

export interface GameConfig {
  mode: GameMode;
  level: Level;
  /** Endless run info. */
  endless?: { seed: number; tier: number };
  /** Share code (custom levels). */
  code?: string;
  /** Editor test-play: called with the winning placement (or null when leaving). */
  onTestDone?: (solution: Mass[] | null) => void;
  /** Editor test-play: where "back" goes. */
  backHref?: string;
}

const TOUCH_LIFT = 46; // CSS px the mass rides above a finger while dragging

interface Pointer {
  id: number;
  type: string;
  startX: number;
  startY: number;
  x: number;
  y: number;
  offX: number;
  offY: number;
  moved: boolean;
  kind: MassKind;
  /** Index in placed (moving) or null (new, from the tray). */
  index: number | null;
  el: HTMLElement;
}

function gridRef(x: number, y: number): string {
  const col = Math.round((x / WORLD_W) * 24);
  const row = Math.round((y / WORLD_H) * 16);
  return `column ${col} of 24, row ${row} of 16`;
}

export function gameScreen(cfg: GameConfig): Screen {
  const level = cfg.level;
  const session = new Session(level);
  const chapterN = Number(level.id.split('-')[0]) || 2;
  const litAt: number[] = level.receivers.map(() => -1);
  let preview: LevelTrace | null = null;
  let previewDirty = false;
  let drag: DragState | null = null;
  let ptr: Pointer | null = null;
  let focusIndex: number | null = null;
  let hintsShown = 0;
  let winShown = false;
  let winTimer = 0;
  let runTimer = 0;
  let inRun = false;
  let lastCaptured = session.trace.captured;
  let lastPreviewLit: boolean[] = session.trace.receivers.map((r) => r.lit);
  let onboarding =
    cfg.mode === 'campaign' && level.id === LEVELS[0]!.id && !store.progress.onboarded;
  let lastTime = 0;
  let toastTimer = 0;
  let resizeObs: ResizeObserver | null = null;
  let lastKey = '';
  let lastDraw = 0;

  // ---------------------------------------------------------------- DOM
  const root = h('section', { class: 'game', 'aria-label': 'Puzzle' });
  const slot = h('div', { class: 'board-slot', 'aria-hidden': 'true' });
  const overlay = h('div', { class: 'board-overlay' });
  const lesson = h('p', { class: 'lesson', id: 'lesson' });
  const toast = h('div', { class: 'toast', role: 'status', hidden: true });
  const trayItems = h('div', {
    class: 'tray-items',
    role: 'group',
    'aria-label': 'Masses in the tray',
  });
  const meta = h('div', { class: 'tray-meta', 'aria-live': 'off' });
  const undoBtn = h(
    'button',
    { class: 'btn', type: 'button', 'aria-label': 'Undo (Ctrl+Z)', title: 'Undo (Ctrl+Z)' },
    icon(ICONS.undo),
    h('span', { text: 'Undo' }),
  );
  const resetBtn = h(
    'button',
    { class: 'btn', type: 'button', 'aria-label': 'Clear the plate', title: 'Clear the plate' },
    icon(ICONS.reset),
    h('span', { text: 'Reset' }),
  );
  const hintBtn = h(
    'button',
    {
      class: 'icon-btn',
      type: 'button',
      'aria-label': 'Hint: reveal one mass (costs a star)',
      title: 'Hint (costs a star)',
    },
    icon(ICONS.hint),
  );
  const helpBtn = h(
    'button',
    { class: 'icon-btn', type: 'button', 'aria-label': 'How to play', title: 'How to play' },
    icon(ICONS.help),
  );
  const setBtn = h(
    'button',
    { class: 'icon-btn', type: 'button', 'aria-label': 'Settings', title: 'Settings' },
    icon(ICONS.gear),
  );
  const win = h('div', {
    class: 'win',
    role: 'dialog',
    'aria-modal': 'false',
    'aria-labelledby': 'win-title',
    hidden: true,
  });
  const desc = h('p', { class: 'sr-only', id: 'board-desc' });

  const backHref = cfg.backHref ?? (cfg.mode === 'campaign' ? '#/levels' : '#/');
  const backLabel =
    cfg.mode === 'campaign' ? 'Levels' : cfg.mode === 'test' ? 'Back to editor' : 'Title';

  function titleText(): HTMLElement {
    if (cfg.mode === 'campaign') {
      return h(
        'h2',
        { class: 'lvl-name' },
        h('span', { class: 'lvl-num', text: levelLabel(level.id) }),
        level.name,
      );
    }
    if (cfg.mode === 'endless') {
      return h(
        'h2',
        { class: 'lvl-name' },
        h('span', { class: 'lvl-num', text: `Tier ${cfg.endless?.tier ?? 1}` }),
        level.name,
      );
    }
    if (cfg.mode === 'test')
      return h(
        'h2',
        { class: 'lvl-name' },
        h('span', { class: 'lvl-num', text: 'Test play' }),
        level.name,
      );
    return h(
      'h2',
      { class: 'lvl-name' },
      h('span', { class: 'lvl-num', text: 'Shared plate' }),
      level.name,
    );
  }

  // ---------------------------------------------------------------- helpers
  const reduced = () => store.reducedMotion;
  const showToast = (text: string) => {
    toast.textContent = text;
    toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => (toast.hidden = true), 2200);
  };

  function describeBoard() {
    const tray = MASS_KINDS.filter((k) => (level.inventory[k] ?? 0) > 0)
      .map(
        (k) =>
          `${level.inventory[k]} ${level.inventory[k] === 1 ? MASS_SPECS[k].name.toLowerCase() : MASS_SPECS[k].plural.toLowerCase()}`,
      )
      .join(', ');
    const tels = level.receivers
      .map(
        (r, i) =>
          `telescope ${i + 1} needs ${colorName(r.color)} light${receiverNeeds(r) > 1 ? `, ${receiverNeeds(r)} beams at once` : ''}`,
      )
      .join('; ');
    const extras = [
      level.walls.length && `${level.walls.length} wall${level.walls.length > 1 ? 's' : ''}`,
      level.rocks.length && `${level.rocks.length} rocks`,
      level.dust.length && `${level.dust.length} dust cloud${level.dust.length > 1 ? 's' : ''}`,
      level.nebulae.length &&
        `${level.nebulae.length} nebula${level.nebulae.length > 1 ? 'e' : ''}`,
      level.fixed.length && `${level.fixed.length} fixed mass${level.fixed.length > 1 ? 'es' : ''}`,
    ]
      .filter(Boolean)
      .join(', ');
    desc.textContent = `${level.sources.length} beacon${level.sources.length > 1 ? 's' : ''}, ${level.receivers.length} telescope${level.receivers.length > 1 ? 's' : ''}${extras ? `, ${extras}` : ''}. ${tels}. Tray: ${tray || 'empty'}. Par weight ${level.par}.`;
  }

  function renderTray() {
    const counts = session.tray;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    trayItems.replaceChildren();
    for (const k of MASS_KINDS) {
      const total = level.inventory[k] ?? 0;
      if (total === 0) continue;
      const left = counts[k];
      const spec = MASS_SPECS[k];
      const b = h(
        'button',
        {
          class: 'slot',
          type: 'button',
          'data-kind': k,
          disabled: left === 0,
          'aria-label': `${spec.name}, weight ${spec.weight}: ${left} of ${total} in the tray. Drag onto the plate, or press Enter to place.`,
        },
        trayIcon(k, 44, dpr),
        h('span', { class: 'count' }, h('b', { text: `×${left}` }), h('span', { text: spec.name })),
      );
      b.addEventListener('pointerdown', (e) => onTrayDown(e, k, b));
      b.addEventListener('click', (e) => {
        // Keyboard activation (pointer taps are handled on pointerup).
        if ((e as MouseEvent).detail === 0) placeFromTray(k, true);
      });
      trayItems.append(b);
    }
    const w = session.weight;
    meta.replaceChildren(
      h('span', { html: `Weight <strong>${w}</strong> · par ${level.par}` }),
      h('span', { html: `Moves <strong>${session.moves}</strong>` }),
      h('span', {
        class: 'label',
        text: session.placed.length
          ? `on track for ${'★'.repeat(starsFor(w, level.par, hintsShown))}`
          : `best ${'★'.repeat(starCap(hintsShown))}`,
      }),
    );
    undoBtn.disabled = !session.canUndo;
    resetBtn.disabled = session.placed.length === 0;
    const canHint = level.solution.length > 0 && hintsShown < level.solution.length;
    hintBtn.disabled = !canHint;
    hintBtn.setAttribute(
      'aria-label',
      canHint
        ? `Hint: reveal one mass (costs a star; ${level.solution.length - hintsShown} left)`
        : 'No more hints',
    );
  }

  function renderProxies() {
    overlay.replaceChildren();
    session.placed.forEach((m, i) => {
      const [x, y] = app.renderer.view.toScreen(m.x, m.y);
      const spec = MASS_SPECS[m.kind];
      const b = h('button', {
        class: 'mass-proxy',
        type: 'button',
        'data-index': String(i),
        style: { left: `${x}px`, top: `${y}px` },
        'aria-label': `${spec.name} at ${gridRef(m.x, m.y)}. Arrow keys move it (Shift for big steps, Alt for fine), Delete returns it to the tray.`,
        'aria-describedby': 'board-desc',
      });
      b.addEventListener('pointerdown', (e) => onMassDown(e, i, b));
      b.addEventListener('keydown', (e) => onMassKey(e, i));
      b.addEventListener('focus', () => {
        focusIndex = i;
        app.requestFrame();
      });
      b.addEventListener('blur', () => {
        if (focusIndex === i) focusIndex = null;
        endRun();
        app.requestFrame();
      });
      overlay.append(b);
    });
  }

  function focusMass(i: number) {
    const b = overlay.querySelector<HTMLButtonElement>(`.mass-proxy[data-index="${i}"]`);
    b?.focus();
  }

  function syncAfterChange(changes: LitChange[], silentTicks = false) {
    preview = null;
    onLitChanges(changes, session.trace, silentTicks);
    renderTray();
    renderProxies();
    checkWin();
    app.requestFrame();
  }

  function onLitChanges(changes: LitChange[], trace: LevelTrace, quiet = false) {
    const now = lastTime || performance.now();
    for (const c of changes) {
      if (c.lit) {
        litAt[c.index] = now;
        audio.chime(c.index);
        const r = level.receivers[c.index]!;
        if (!reduced()) app.renderer.burst(r.x, r.y, GOLD_BRIGHT, 14);
        buzz(10, store.settings.haptics);
      } else {
        litAt[c.index] = -1;
        if (!quiet) audio.dim();
      }
    }
    const litCount = trace.receivers.filter((r) => r.lit).length;
    audio.padBrightness(litCount / Math.max(1, level.receivers.length));
    if (trace.captured > lastCaptured) audio.whoomp();
    lastCaptured = trace.captured;
    if (changes.length && !trace.won) {
      announce(
        changes
          .map((c) => `Telescope ${c.index + 1} ${c.lit ? 'lit' : 'dark'}`)
          .concat(`${litCount} of ${level.receivers.length} lit`)
          .join('. '),
      );
    }
  }

  // ---------------------------------------------------------------- placing
  function placeFromTray(kind: MassKind, fromKeyboard: boolean) {
    if (session.tray[kind] <= 0) return;
    stopOnboarding();
    const m = session.freeSpot(kind);
    const changes = session.commit([...session.placed, m]);
    audio.tick('drop');
    syncAfterChange(changes);
    const i = session.placed.length - 1;
    if (fromKeyboard) {
      focusMass(i);
      announce(
        `${MASS_SPECS[kind].name} placed at ${gridRef(m.x, m.y)}. Move it with the arrow keys.`,
      );
    }
  }

  function removeMass(i: number) {
    const m = session.placed[i];
    if (!m) return;
    const changes = session.commit(session.placed.filter((_, j) => j !== i));
    audio.tick('lift');
    syncAfterChange(changes);
    announce(`${MASS_SPECS[m.kind].name} returned to the tray.`);
    if (session.placed.length) focusMass(Math.min(i, session.placed.length - 1));
    else trayItems.querySelector<HTMLButtonElement>(`.slot[data-kind="${m.kind}"]`)?.focus();
  }

  // ---------------------------------------------------------------- pointer drag
  function insideTray(x: number, y: number): boolean {
    const tray = root.querySelector('.tray') as HTMLElement;
    const r = tray.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  function onPlate(x: number, y: number): boolean {
    const p = app.renderer.view.plate;
    return x >= p.x && x <= p.x + p.w && y >= p.y && y <= p.y + p.h;
  }

  function startPointer(e: PointerEvent, kind: MassKind, index: number | null, el: HTMLElement) {
    if (!e.isPrimary || e.button > 0 || ptr) return;
    e.preventDefault();
    let offX = 0;
    let offY = 0;
    if (index !== null) {
      const m = session.placed[index]!;
      const [sx, sy] = app.renderer.view.toScreen(m.x, m.y);
      offX = sx - e.clientX;
      offY = sy - e.clientY;
    }
    ptr = {
      id: e.pointerId,
      type: e.pointerType,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      offX,
      offY,
      moved: false,
      kind,
      index,
      el,
    };
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // Older browsers: fall back to window listeners below.
    }
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
  }

  function onTrayDown(e: PointerEvent, kind: MassKind, el: HTMLElement) {
    if (session.tray[kind] <= 0) return;
    startPointer(e, kind, null, el);
  }

  function onMassDown(e: PointerEvent, i: number, el: HTMLElement) {
    startPointer(e, session.placed[i]!.kind, i, el);
  }

  function beginDrag() {
    if (!ptr) return;
    ptr.moved = true;
    stopOnboarding();
    endRun();
    hideWin(false);
    if (ptr.type === 'touch') {
      // Ride above the finger so the beams around the mass stay visible.
      ptr.offX = 0;
      ptr.offY = -TOUCH_LIFT;
    } else if (ptr.index === null) {
      ptr.offX = 0;
      ptr.offY = 0;
    }
    ptr.el.classList.add('dragging');
    audio.tick('lift');
    updateDrag();
  }

  function updateDrag() {
    if (!ptr || !ptr.moved) return;
    const sx = ptr.x + ptr.offX;
    const sy = ptr.y + ptr.offY;
    const [wx, wy] = app.renderer.view.toWorld(sx, sy);
    const overTray = insideTray(ptr.x, ptr.y);
    const plate = onPlate(sx, sy) && !overTray;
    const valid = plate && session.canPlaceAt(ptr.kind, wx, wy, ptr.index);
    drag = { kind: ptr.kind, x: wx, y: wy, valid, index: ptr.index };
    previewDirty = true;
    app.requestFrame();
  }

  function onPointerMove(e: PointerEvent) {
    if (!ptr || e.pointerId !== ptr.id) return;
    ptr.x = e.clientX;
    ptr.y = e.clientY;
    if (!ptr.moved && Math.hypot(ptr.x - ptr.startX, ptr.y - ptr.startY) > 6) beginDrag();
    else if (ptr.moved) updateDrag();
  }

  function finishPointer() {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerCancel);
    ptr?.el.classList.remove('dragging');
    ptr = null;
    drag = null;
    preview = null;
  }

  function onPointerCancel(e: PointerEvent) {
    if (!ptr || e.pointerId !== ptr.id) return;
    finishPointer();
    renderProxies();
    checkWin();
    app.requestFrame();
  }

  function onPointerUp(e: PointerEvent) {
    if (!ptr || e.pointerId !== ptr.id) return;
    const p = ptr;
    if (!p.moved) {
      finishPointer();
      if (p.index === null) placeFromTray(p.kind, false);
      else focusMass(p.index);
      return;
    }
    const sx = p.x + p.offX;
    const sy = p.y + p.offY;
    const [wx, wy] = app.renderer.view.toWorld(sx, sy);
    const overTray = insideTray(p.x, p.y);
    const plate = onPlate(sx, sy) && !overTray;
    finishPointer();
    if (!plate) {
      if (p.index !== null) removeMass(p.index);
      else {
        audio.tick('ui');
        checkWin();
        app.requestFrame();
      }
      return;
    }
    if (!session.canPlaceAt(p.kind, wx, wy, p.index)) {
      audio.tick('nope');
      buzz([12, 40, 12], store.settings.haptics);
      showToast('Too close to something solid. Masses need clear space.');
      announce('Cannot place there: too close to another object.');
      renderProxies();
      checkWin();
      app.requestFrame();
      return;
    }
    const m: Mass = { kind: p.kind, x: Math.round(wx * 10) / 10, y: Math.round(wy * 10) / 10 };
    const next =
      p.index === null
        ? [...session.placed, m]
        : session.placed.map((q, j) => (j === p.index ? m : q));
    const changes = session.commit(next);
    audio.tick('drop');
    syncAfterChange(changes, true);
  }

  // ---------------------------------------------------------------- keyboard
  function endRun() {
    window.clearTimeout(runTimer);
    inRun = false;
  }

  function onMassKey(e: KeyboardEvent, i: number) {
    const m = session.placed[i];
    if (!m) return;
    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const dir = arrows[e.key];
    if (dir) {
      e.preventDefault();
      const step = e.shiftKey ? 25 : e.altKey ? 1 : 5;
      const [dx, dy] = app.renderer.view.screenStepToWorld(dir[0] * step, dir[1] * step);
      const nx = m.x + dx;
      const ny = m.y + dy;
      if (!session.canPlaceAt(m.kind, nx, ny, i)) {
        audio.tick('nope');
        announce('Blocked.');
        return;
      }
      stopOnboarding();
      if (!inRun) {
        session.beginRun();
        inRun = true;
      }
      window.clearTimeout(runTimer);
      runTimer = window.setTimeout(endRun, 900);
      const changes = session.amend(
        session.placed.map((q, j) => (j === i ? { ...q, x: nx, y: ny } : q)),
      );
      preview = null;
      onLitChanges(changes, session.trace, true);
      renderTray();
      // Move the proxy without rebuilding (keeps focus).
      const b = overlay.querySelector<HTMLElement>(`.mass-proxy[data-index="${i}"]`);
      if (b) {
        const [sx, sy] = app.renderer.view.toScreen(nx, ny);
        b.style.left = `${sx}px`;
        b.style.top = `${sy}px`;
        b.setAttribute(
          'aria-label',
          `${MASS_SPECS[m.kind].name} at ${gridRef(nx, ny)}. Arrow keys move it (Shift for big steps, Alt for fine), Delete returns it to the tray.`,
        );
      }
      checkWin();
      app.requestFrame();
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      endRun();
      removeMass(i);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      endRun();
      if (session.won) {
        if (win.hidden) showWin(starsFor(session.weight, level.par, hintsShown), false);
      } else {
        const lit = session.trace.receivers.filter((r) => r.lit).length;
        announce(
          `${MASS_SPECS[m.kind].name} at ${gridRef(m.x, m.y)}. ${lit} of ${level.receivers.length} telescopes lit.`,
        );
      }
    } else if (e.key === 'Escape') {
      endRun();
      (e.target as HTMLElement).blur();
    }
  }

  function onGlobalKey(e: KeyboardEvent) {
    if (document.querySelector('dialog[open]')) return;
    const target = e.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      doUndo();
    } else if (e.key === 'Escape' && !win.hidden) {
      hideWin(true);
    }
  }

  // ---------------------------------------------------------------- actions
  function doUndo() {
    endRun();
    const changes = session.undo();
    if (!changes) return;
    audio.tick('ui');
    syncAfterChange(changes);
    announce('Undone.');
  }

  async function doReset() {
    endRun();
    const changes = session.reset();
    audio.tick('ui');
    syncAfterChange(changes);
    announce('Plate cleared. Undo brings the masses back.');
  }

  async function doHint() {
    if (hintsShown >= level.solution.length) return;
    if (hintsShown === 0) {
      const ok = await confirmDialog(
        'Reveal one mass?',
        `A hint shows where one mass of the reference solution goes. Each hint costs one star on this plate (best possible becomes ${starCap(1)}).`,
        'Reveal',
      );
      if (!ok) return;
    }
    hintsShown++;
    const m = level.solution[hintsShown - 1]!;
    renderTray();
    app.requestFrame();
    announce(`Hint: a ${MASS_SPECS[m.kind].name.toLowerCase()} belongs near ${gridRef(m.x, m.y)}.`);
  }

  // ---------------------------------------------------------------- win
  function declareWin() {
    if (winShown) return;
    winShown = true;
    const stars = starsFor(session.weight, level.par, hintsShown);
    audio.chord(level.receivers.length);
    buzz([20, 50, 30, 50, 60], store.settings.haptics);
    if (!reduced()) {
      level.receivers.forEach((r) => app.renderer.burst(r.x, r.y, GOLD_BRIGHT, 26));
    }
    let improved = false;
    if (cfg.mode === 'campaign') improved = store.recordWin(level.id, stars, session.weight);
    if (cfg.mode === 'endless') store.recordEndless(true);
    announce(`All telescopes lit. Plate complete with ${stars} of 3 stars.`);
    window.clearTimeout(winTimer);
    winTimer = window.setTimeout(() => showWin(stars, improved), reduced() ? 0 : 750);
    app.requestFrame();
  }

  function showWin(stars: number, improved: boolean) {
    if (!session.won) return;
    const idx = LEVELS.findIndex((l) => l.id === level.id);
    const next = idx >= 0 ? LEVELS[idx + 1] : undefined;
    const actions: HTMLElement[] = [];
    if (cfg.mode === 'campaign') {
      if (next)
        actions.push(
          h(
            'a',
            { class: 'btn primary', href: `#/play/${next.id}` },
            'Next plate',
            icon(ICONS.next),
          ),
        );
      else
        actions.push(
          h('a', { class: 'btn primary', href: '#/endless' }, 'Try endless mode', icon(ICONS.next)),
        );
      actions.push(
        h(
          'button',
          { class: 'btn', type: 'button', onclick: () => app.navigate(location.hash) },
          'Replay',
        ),
      );
      actions.push(h('a', { class: 'btn ghost', href: '#/levels' }, 'Levels'));
    } else if (cfg.mode === 'endless') {
      const tierNext = Math.min(6, 1 + Math.floor(store.progress.endless.streak / 2));
      const seed = Math.floor(Math.random() * 1e9);
      actions.push(
        h(
          'a',
          { class: 'btn primary', href: `#/endless/${seed}/${tierNext}` },
          'Next puzzle',
          icon(ICONS.next),
        ),
      );
      actions.push(
        h(
          'button',
          { class: 'btn', type: 'button', onclick: () => app.navigate(location.hash) },
          'Again',
        ),
      );
      actions.push(h('a', { class: 'btn ghost', href: '#/' }, 'Title'));
    } else if (cfg.mode === 'test') {
      actions.push(
        h(
          'button',
          {
            class: 'btn primary',
            type: 'button',
            onclick: () =>
              cfg.onTestDone?.(
                session.placed.map((m) => ({
                  kind: m.kind,
                  x: Math.round(m.x),
                  y: Math.round(m.y),
                })),
              ),
          },
          'Save as reference solution',
        ),
      );
      actions.push(
        h(
          'button',
          { class: 'btn', type: 'button', onclick: () => cfg.onTestDone?.(null) },
          'Back to editor',
        ),
      );
    } else {
      actions.push(
        h(
          'a',
          { class: 'btn primary', href: `#/editor/${cfg.code ?? encodeLevel(level)}` },
          icon(ICONS.edit),
          'Edit this plate',
        ),
      );
      actions.push(h('a', { class: 'btn ghost', href: '#/' }, 'Title'));
    }
    const detail = `Weight ${session.weight} · par ${level.par} · ${session.moves} move${session.moves === 1 ? '' : 's'}${hintsShown ? ` · ${hintsShown} hint${hintsShown > 1 ? 's' : ''}` : ''}`;
    const nudge =
      stars < 3 && hintsShown === 0 && session.weight > level.par
        ? `Three stars need a total weight of ${level.par} or less.`
        : improved
          ? 'New best for this plate.'
          : cfg.mode === 'test'
            ? 'Save this placement so others get hints and a par.'
            : '';
    win.replaceChildren(
      h('h2', { id: 'win-title', text: 'All telescopes lit' }),
      starIcons(stars, 3, 'win-stars'),
      h('p', { text: detail }),
      nudge ? h('p', { text: nudge }) : '',
      h('div', { class: 'win-actions' }, ...actions),
    );
    win.classList.toggle('animate', !reduced());
    win.hidden = false;
    const primary = win.querySelector<HTMLElement>('.btn.primary');
    primary?.focus({ preventScroll: true });
  }

  /** After every change: celebrate a new win once, re-show the card, or tidy up a lost win. */
  function checkWin() {
    if (session.won) {
      if (!winShown) declareWin();
      else if (win.hidden && !ptr) {
        window.clearTimeout(winTimer);
        winTimer = window.setTimeout(
          () => showWin(starsFor(session.weight, level.par, hintsShown), false),
          200,
        );
      }
    } else {
      winShown = false;
      window.clearTimeout(winTimer);
      win.hidden = true;
    }
  }

  function hideWin(byUser: boolean) {
    if (win.hidden) return;
    win.hidden = true;
    if (byUser) focusMass(Math.max(0, session.placed.length - 1));
  }

  // ---------------------------------------------------------------- onboarding
  function stopOnboarding() {
    if (!onboarding) return;
    onboarding = false;
    store.setOnboarded();
    app.requestFrame();
  }

  function ghost(): { from: [number, number]; to: [number, number]; kind: MassKind } | null {
    if (!onboarding || session.placed.length > 0) return null;
    const btn = trayItems.querySelector('.slot');
    const target = level.solution[0];
    if (!btn || !target) return null;
    const r = btn.getBoundingClientRect();
    return {
      from: [r.left + 28, r.top + r.height / 2],
      to: [target.x, target.y],
      kind: target.kind,
    };
  }

  // ---------------------------------------------------------------- screen
  return {
    title: cfg.mode === 'campaign' ? `${levelLabel(level.id)} ${level.name}` : level.name,
    mount(container) {
      audio.setKey(CHAPTER_KEYS[chapterN] ?? CHAPTER_KEYS[2]!);
      audio.requestPad(true);
      const hud = h(
        'header',
        { class: 'hud' },
        h(
          'a',
          {
            class: 'icon-btn',
            href: backHref,
            'aria-label': backLabel,
            title: backLabel,
            onclick: (e: Event) => {
              if (cfg.mode === 'test') {
                e.preventDefault();
                cfg.onTestDone?.(null);
              }
            },
          },
          icon(cfg.mode === 'campaign' ? ICONS.grid : ICONS.back),
        ),
        h(
          'div',
          { class: 'titleblock' },
          h('h1', { class: 'wordmark', text: 'Lensing' }),
          titleText(),
        ),
        h('div', { class: 'actions' }, hintBtn, helpBtn, setBtn),
      );
      lesson.textContent =
        level.hint ??
        (cfg.mode === 'endless'
          ? 'Light every telescope. This puzzle was generated around hidden masses: the tray holds what you need.'
          : 'Light every telescope at once.');
      const tray = h(
        'aside',
        { class: 'tray', 'aria-label': 'Tray and actions' },
        h('h2', { class: 'tray-title label', text: 'Tray' }),
        trayItems,
        meta,
        h('div', { class: 'tray-actions' }, undoBtn, resetBtn),
      );
      root.append(hud, lesson, slot, tray, overlay, win, toast, desc);
      container.append(root);
      undoBtn.addEventListener('click', doUndo);
      resetBtn.addEventListener('click', () => void doReset());
      hintBtn.addEventListener('click', () => void doHint());
      helpBtn.addEventListener('click', () => openHelp('play'));
      setBtn.addEventListener('click', () => openSettings());
      window.addEventListener('keydown', onGlobalKey);
      app.renderer.setLevel(level);
      describeBoard();
      renderTray();
      this.layout?.();
      // Lesson text, tray contents or the viewport can change the plate's space: refit.
      resizeObs = new ResizeObserver(() => {
        this.layout?.();
        app.requestFrame();
      });
      resizeObs.observe(slot);
      markReady();
      if (cfg.mode === 'campaign' && level.id === LEVELS[0]!.id && session.placed.length === 0) {
        announce(`${level.name}. ${level.hint ?? ''}`);
      }
    },
    unmount() {
      resizeObs?.disconnect();
      window.removeEventListener('keydown', onGlobalKey);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      window.clearTimeout(winTimer);
      window.clearTimeout(runTimer);
      window.clearTimeout(toastTimer);
      audio.requestPad(false);
      app.renderer.setLevel(null);
    },
    layout() {
      const r = slot.getBoundingClientRect();
      app.renderer.setSlot({ x: r.left, y: r.top, w: r.width, h: r.height }, true);
      const p = app.renderer.view.plate;
      // Plate geometry for tests and tools: x, y, width, height (CSS px) and rotation.
      root.dataset.plate =
        [p.x, p.y, p.w, p.h].map((v) => v.toFixed(2)).join(',') +
        (app.renderer.view.rotated ? ',r' : ',n');
      renderProxies();
      if (ptr?.moved) updateDrag();
    },
    frame(time) {
      lastTime = time;
      // When only the shimmer is moving, ~30 fps is plenty; anything interactive draws at once.
      const key = `${session.moves}|${session.placed.reduce((s, m) => s + m.x * 3 + m.y, 0)}|${focusIndex}|${hintsShown}|${onboarding}|${winShown}|${app.renderer.view.plate.x},${app.renderer.view.plate.w}`;
      const busy =
        !!drag || app.renderer.hasParticles || litAt.some((t) => t >= 0 && time - t < 800);
      if (!busy && key === lastKey && time - lastDraw < 30 && !reduced()) return true;
      lastKey = key;
      lastDraw = time;
      if (drag && previewDirty) {
        previewDirty = false;
        const [dsx, dsy] = app.renderer.view.toScreen(drag.x, drag.y);
        const offPlate = !onPlate(dsx, dsy) || (ptr !== null && insideTray(ptr.x, ptr.y));
        preview = session.preview(
          offPlate ? null : { kind: drag.kind, x: drag.x, y: drag.y },
          drag.index,
        );
        // Live feedback while dragging: chimes as telescopes light, whoomp on capture.
        const lit = preview.receivers.map((r) => r.lit);
        lit.forEach((on, i) => {
          if (on && !lastPreviewLit[i]) {
            audio.chime(i);
            litAt[i] = time;
          } else if (!on && lastPreviewLit[i]) {
            litAt[i] = -1;
          }
        });
        lastPreviewLit = lit;
        if (preview.captured > lastCaptured) audio.whoomp();
        lastCaptured = preview.captured;
        audio.padBrightness(lit.filter(Boolean).length / Math.max(1, level.receivers.length));
      } else if (!drag) {
        lastPreviewLit = session.trace.receivers.map((r) => r.lit);
      }
      const trace = drag && preview ? preview : session.trace;
      const hints = level.solution.slice(0, hintsShown);
      app.renderer.render(
        {
          level,
          placed: session.placed,
          trace,
          drag,
          focus: focusIndex,
          hints,
          litAt,
          ghost: ghost(),
        },
        app.renderOptions(time),
      );
      const blooming = litAt.some((t) => t >= 0 && time - t < 800);
      return !reduced() || app.renderer.hasParticles || blooming || !!drag;
    },
  };
}
